// Event Analytics Service
// Tracks form performance, user behavior, and conversion funnels

const EVENT_TYPES = {
  FORM_VIEW: 'form_view',
  FORM_START: 'form_start',
  FIELD_FOCUS: 'field_focus',
  FIELD_COMPLETE: 'field_complete',
  FIELD_ABANDON: 'field_abandon',
  FORM_SUBMIT: 'form_submit',
  FORM_ABANDON: 'form_abandon',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_COMPLETE: 'payment_complete',
  PAYMENT_FAILED: 'payment_failed',
};

// Generate time-series data for the past N days
const generateTimeSeries = (days, baseValue, variance = 0.3) => {
  const data = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date?.setDate(date?.getDate() - i);
    const label = date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const value = Math.round(baseValue * (1 + (Math.random() - 0.5) * variance));
    data?.push({ date: label, value });
  }
  return data;
};

// KPI summary data
export const getEventKPIs = () => ({
  totalEvents: { value: 48_320, change: '+18.4%', positive: true },
  formViews: { value: 12_840, change: '+9.2%', positive: true },
  conversionRate: { value: '34.7%', change: '+2.1%', positive: true },
  avgTimeOnForm: { value: '2m 14s', change: '-8s', positive: true },
  abandonRate: { value: '41.3%', change: '-3.8%', positive: true },
  paymentSuccess: { value: '91.2%', change: '+1.4%', positive: true },
});

// Conversion funnel stages
export const getConversionFunnel = () => [
  { stage: 'Form Views', count: 12840, pct: 100, color: '#107570' },
  { stage: 'Form Started', count: 8960, pct: 69.8, color: '#0d9488' },
  { stage: 'Fields Completed', count: 6210, pct: 48.4, color: '#14b8a6' },
  { stage: 'Form Submitted', count: 4456, pct: 34.7, color: '#2dd4bf' },
  { stage: 'Payment Initiated', count: 1820, pct: 14.2, color: '#5eead4' },
  { stage: 'Payment Complete', count: 1660, pct: 12.9, color: '#99f6e4' },
];

// Top performing forms
export const getTopForms = () => [
  { id: 1, name: 'Contact Us', views: 3240, submissions: 1890, rate: '58.3%', trend: '+12%' },
  { id: 2, name: 'Product Order Form', views: 2810, submissions: 1120, rate: '39.9%', trend: '+7%' },
  { id: 3, name: 'Event Registration', views: 2100, submissions: 980, rate: '46.7%', trend: '+21%' },
  { id: 4, name: 'Newsletter Signup', views: 1950, submissions: 1430, rate: '73.3%', trend: '+5%' },
  { id: 5, name: 'Job Application', views: 1640, submissions: 410, rate: '25.0%', trend: '-3%' },
  { id: 6, name: 'Feedback Survey', views: 1100, submissions: 626, rate: '56.9%', trend: '+9%' },
];

// Field-level drop-off analysis
export const getFieldDropOff = () => [
  { field: 'Full Name', completions: 8820, dropOff: 140, dropPct: 1.6 },
  { field: 'Email Address', completions: 8540, dropOff: 280, dropPct: 3.2 },
  { field: 'Phone Number', completions: 7680, dropOff: 860, dropPct: 10.1 },
  { field: 'Company Name', completions: 7120, dropOff: 560, dropPct: 7.3 },
  { field: 'Message / Notes', completions: 6210, dropOff: 910, dropPct: 12.8 },
  { field: 'File Upload', completions: 5440, dropOff: 770, dropPct: 14.2 },
  { field: 'Payment Details', completions: 4456, dropOff: 984, dropPct: 18.1 },
];

// User behavior heatmap data (hour × day)
export const getBehaviorHeatmap = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  return days?.map(day => ({
    day,
    hours: hours?.map(hour => ({
      hour: `${hour}:00`,
      value: Math.round(Math.random() * 400 + (day === 'Sat' || day === 'Sun' ? 20 : 80)),
    })),
  }));
};

// Event volume time series (30 days)
export const getEventTimeSeries = () => ({
  views: generateTimeSeries(30, 428, 0.4),
  submissions: generateTimeSeries(30, 148, 0.35),
  payments: generateTimeSeries(30, 55, 0.5),
});

// Device & browser breakdown
export const getDeviceBreakdown = () => ({
  devices: [
    { name: 'Desktop', value: 54, color: '#107570' },
    { name: 'Mobile', value: 38, color: '#14b8a6' },
    { name: 'Tablet', value: 8, color: '#5eead4' },
  ],
  browsers: [
    { name: 'Chrome', value: 62 },
    { name: 'Safari', value: 21 },
    { name: 'Firefox', value: 9 },
    { name: 'Edge', value: 6 },
    { name: 'Other', value: 2 },
  ],
});

// Recent events stream
export const getRecentEvents = () => [
  { id: 1, type: EVENT_TYPES?.FORM_SUBMIT, form: 'Contact Us', user: 'user_8821', time: '2s ago', meta: 'Desktop · Chrome' },
  { id: 2, type: EVENT_TYPES?.PAYMENT_COMPLETE, form: 'Product Order', user: 'user_4412', time: '14s ago', meta: '$49.00 · Visa' },
  { id: 3, type: EVENT_TYPES?.FORM_ABANDON, form: 'Job Application', user: 'user_9034', time: '31s ago', meta: 'Dropped at: File Upload' },
  { id: 4, type: EVENT_TYPES?.FORM_START, form: 'Newsletter Signup', user: 'user_2201', time: '45s ago', meta: 'Mobile · Safari' },
  { id: 5, type: EVENT_TYPES?.PAYMENT_FAILED, form: 'Product Order', user: 'user_7710', time: '1m ago', meta: 'Declined · Mastercard' },
  { id: 6, type: EVENT_TYPES?.FORM_SUBMIT, form: 'Event Registration', user: 'user_3318', time: '2m ago', meta: 'Desktop · Firefox' },
  { id: 7, type: EVENT_TYPES?.FORM_VIEW, form: 'Feedback Survey', user: 'user_5509', time: '3m ago', meta: 'Tablet · Chrome' },
  { id: 8, type: EVENT_TYPES?.FORM_SUBMIT, form: 'Contact Us', user: 'user_1102', time: '4m ago', meta: 'Desktop · Edge' },
];

export { EVENT_TYPES };
